import { request } from "../api";

export type BootstrapTokenValidityMode =
  | "permanent"
  | "rotating"
  | "limited_use"
  | "short_lived";

export type BootstrapTokenStatus = "active" | "revoked" | "expired";

export interface BootstrapToken {
  id: string;
  label: string;
  validity_mode: BootstrapTokenValidityMode;
  max_uses: number | null;
  used_count: number;
  status: BootstrapTokenStatus;
  is_company_default: boolean;
  qr_available: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BootstrapQrPayload {
  v: number;
  kind: "bootstrap";
  api: string;
  bootstrap_code: string;
  company_slug: string;
  cleartext: boolean;
  verify_tls: boolean;
}

export interface BootstrapTokenDetail extends BootstrapToken {
  raw_code: string;
  qr_payload: BootstrapQrPayload | null;
}

export interface IssuedBootstrapToken {
  token: BootstrapToken;
  raw_code: string;
  qr_payload: BootstrapQrPayload;
}

export interface CreateBootstrapTokenInput {
  label: string;
  validity_mode?: BootstrapTokenValidityMode;
  max_uses?: number | null;
  expires_at?: string | null;
}

interface PaginatedList<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

function unwrapList<T>(payload: PaginatedList<T> | T[]): T[] {
  if (Array.isArray(payload)) return payload;
  return payload.results ?? [];
}

export const bootstrapTokensApi = {
  list: async (): Promise<BootstrapToken[]> => {
    const data = await request<PaginatedList<BootstrapToken> | BootstrapToken[]>(
      "/api/admin/v1/bootstrap-tokens/",
    );
    return unwrapList(data);
  },

  create: (input: CreateBootstrapTokenInput) =>
    request<IssuedBootstrapToken>("/api/admin/v1/bootstrap-tokens/", {
      method: "POST",
      json: input,
    }),

  get: (id: string) =>
    request<BootstrapTokenDetail>(`/api/admin/v1/bootstrap-tokens/${id}/`),

  revoke: (id: string) =>
    request<BootstrapToken>(`/api/admin/v1/bootstrap-tokens/${id}/revoke/`, {
      method: "POST",
    }),

  rotate: (id: string) =>
    request<IssuedBootstrapToken>(
      `/api/admin/v1/bootstrap-tokens/${id}/rotate/`,
      { method: "POST" },
    ),
};
